import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import withStyles from 'src/common/withStyles';
import { Helmet } from 'react-helmet-async';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useHistory } from 'react-router-dom';

import { decorateComponent, unpackApiError } from '../../../../common/helpers';
import { ReduxState } from '../../../../types/reduxState';
import Button from '../../../../common/components/Button';
import { MFAMethod } from '../../../../types/common/MFAMethods';
import { resendMFACodeAction, setMFAMethod } from '../../../../common/ducks/user';
import { PATHS } from '../../../../common/constants';
import FormError from '../../../../common/components/FormError';

import s from './ChooseMFAMethod.module.scss';

const title = 'Two-factor authentication';
const subtitle = 'Select which two-factor authentication method you would like to use?';
const methodLabels = {
  totp: 'Authenticator app (TOTP)',
  sms: 'Text',
  push: 'Push Notification',
};

const ChooseMFAMethod: FC = () => {
  const dispatch = useDispatch<ThunkDispatch<ReduxState, {}, Action>>();
  const { push } = useHistory();
  const currentMFAMethods = useSelector<ReduxState, MFAMethod[] | null | undefined>(({ user }) => user.get('userMFAMethods', null));
  const [resendError, setResendError] = useState<string | null>(null);
  const onMethodClick = useCallback(async (method: MFAMethod) => {
    dispatch(setMFAMethod(method));
    if (method !== 'totp') {
      dispatch(resendMFACodeAction({ method }))
        .then(() => {
          if (resendError) setResendError(null);
          push(PATHS.OTP);
        })
        .catch(async (error) => {
          const { detail } = await unpackApiError(error);
          setResendError(detail || 'Error resending code');
        });
    } else {
      push(PATHS.OTP);
    }
  }, [dispatch, push, resendError]);

  useEffect(() => {
    if (!(currentMFAMethods && currentMFAMethods.length)) {
      push(PATHS.LOGIN);
    }
  }, [currentMFAMethods, push]);
  return (
    <div className={s.container}>
      <Helmet>
        <title lang="en">{title}</title>
      </Helmet>
      <h1 className={s.title}>{title}</h1>
      <p className={s.message}>{subtitle}</p>
      <FormError error={resendError} />
      {currentMFAMethods && currentMFAMethods.length > 1 && currentMFAMethods.map(option => (
        <Button
          key={option}
          type="button"
          onClick={() => { onMethodClick(option); }}
        >
          {methodLabels[option]}
        </Button>
      ))}
    </div>
  );
};

const decorators = [
  withStyles(s),
];

export default decorateComponent(ChooseMFAMethod, decorators);
